import { z } from "zod";

import { isoDateTimeSchema, uuidSchema } from "./primitives.js";

/**
 * 后台账号与口令。
 *
 * 这一块的性质与商品不同：**它管的是权限本身**。因此规则写得比别处严——
 * 口令长度写进契约（前后端用同一条），改口令一律要先验当前口令，
 * 停用账号不删除（留着审计痕迹）。
 */

/**
 * 后台口令。长度下限与 "至少 10 位" 的理由：后台能改价格与上架内容，
 * 而登录口是暴露在公网上的，短口令等于没有口令。
 */
export const adminPasswordSchema = z.string().min(10, { error: "口令至少 10 位" }).max(128);

/** 账号名：小写字母、数字、连字符与下划线，便于在日志与对账里辨认。 */
export const adminUsernameSchema = z
  .string()
  .min(2)
  .max(32)
  .regex(/^[a-z0-9_-]+$/, { error: "账号只能用英文小写、数字、连字符与下划线" });

/** 改自己的口令：必须先给出当前口令。 */
export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: adminPasswordSchema,
});

export type ChangePasswordRequestPayload = z.infer<typeof changePasswordRequestSchema>;

/** 后台账号（管理用）。停用的账号保留记录，不能登录，也不能删除。 */
export const adminUserSchema = z.object({
  id: uuidSchema,
  username: z.string().min(1).max(64),
  displayName: z.string().min(1).max(64),
  role: z.enum(["owner", "operator"]),
  disabled: z.boolean(),
  createdAt: isoDateTimeSchema,
  lastLoginAt: isoDateTimeSchema.nullable(),
});

export type AdminUserPayload = z.infer<typeof adminUserSchema>;

export const adminUserListResponseSchema = z.object({
  items: z.array(adminUserSchema),
  total: z.int().nonnegative(),
});

export type AdminUserListResponsePayload = z.infer<typeof adminUserListResponseSchema>;

export const createAdminUserRequestSchema = z.object({
  username: adminUsernameSchema,
  displayName: z.string().min(1).max(64),
  role: z.enum(["owner", "operator"]),
  password: adminPasswordSchema,
});

export type CreateAdminUserRequestPayload = z.infer<typeof createAdminUserRequestSchema>;

/**
 * 改账号：改显示名、角色、停用，以及**重置别人的口令**（店主代运营找回）。
 * 自己改自己的口令走 `changePasswordRequestSchema`，那条路要求先验当前口令。
 */
export const updateAdminUserRequestSchema = z.object({
  displayName: z.string().min(1).max(64).optional(),
  role: z.enum(["owner", "operator"]).optional(),
  disabled: z.boolean().optional(),
  newPassword: adminPasswordSchema.optional(),
});

export type UpdateAdminUserRequestPayload = z.infer<typeof updateAdminUserRequestSchema>;
